import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "../../Assets/Logos/Logo.svg";
import { 
  FaUserCircle, 
  FaSignOutAlt, 
  FaChevronDown,
} from 'react-icons/fa';
import { TbLayoutDashboard } from "react-icons/tb";
import './AuthNavbar.css';
import { HiDocument } from "react-icons/hi";
import { MdSecurity } from "react-icons/md";

const AuthNavbar = ({ setIsSidebarOpen }) => {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const userName = userData?.name || "User";
  const userEmail = userData?.email || "user@example.com";
  const userInitials = userName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    // Clear all auth data
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("sidebar");
    localStorage.removeItem("permissions");
    
    // Show logout animation
    setIsDropdownOpen(false);
    
    // Redirect to home
    navigate("/");
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogoClick = () => {
    if (window.innerWidth < 1024) {
      setIsSidebarOpen(prevState => !prevState);
    } else {
      navigate("/dashboard");
    }
  };

  return (
    <>
      <nav className={`
        fixed top-0 w-full z-50 transition-all duration-300
        ${scrolled 
          ? "bg-white/95  backdrop-blur-md shadow-lg py-3" 
          : "bg-white  shadow-md py-4"
        }
      `}>
        <div className="px-4 mx-auto flex justify-between items-center">
          {/* Left Section - Logo */}
          <div className="flex items-center">
            <div className="relative group">
              {/* Animated gradient ring */}
              {/* <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg blur opacity-0 group-hover:opacity-70 transition-opacity duration-300"></div> */}
              
              <img
                src={logo}
                alt="PM Surya Ghar"
                className="relative h-12 w-auto cursor-pointer transform transition-all duration-300 hover:scale-105"
                onClick={handleLogoClick}
              />
            </div>

          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-4">

            {/* User Menu */}
            <div className="relative">
              <button
                ref={buttonRef}
                onClick={toggleDropdown}
                className="flex items-center space-x-3 focus:outline-none group"
              >
                {/* User Avatar with Gradient */}
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full blur opacity-0 group-hover:opacity-70 transition-opacity duration-300"></div>
                  
                  <div className="relative flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold text-sm">
                    {userInitials}
                  </div>

                  {/* Online Status */}
                  <span className="absolute bottom-0 right-0 block w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-900 rounded-full"></span>
                </div>

                {/* User Info - Hidden on mobile */}
                <div className="hidden md:block text-left">
                  <p className="text-sm font-semibold text-gray-700">{userName}</p>
                  <p className="text-xs text-gray-500 ">{userEmail}</p>
                </div>

                {/* Dropdown Icon */}
                <FaChevronDown className={`
                  w-3 h-3 text-gray-500 dark:text-gray-400 transition-all duration-300
                  ${isDropdownOpen ? "rotate-180" : ""}
                `} />
              </button>

              {/* Enhanced Dropdown Menu */}
              {isDropdownOpen && (
                <div 
                  ref={dropdownRef}
                  className="absolute right-0 mt-3 w-72 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-100 dark:border-gray-700 overflow-hidden animate-slideDown"
                >
                  {/* User Header */}
                  <div className="p-4 bg-gradient-to-r from-yellow-500 to-orange-500">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white/20 backdrop-blur text-white font-bold text-lg">
                        {userInitials}
                      </div>
                      <div className="text-white">
                        <p className="font-semibold">{userName}</p>
                        <p className="text-xs opacity-90">{userEmail}</p>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="p-2">
                    <Link
                      to="/dashboard"
                      onClick={toggleDropdown}
                      className="flex items-center space-x-3 px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-yellow-50 dark:hover:bg-gray-700 rounded-lg transition-all duration-300 group"
                    >
                      <TbLayoutDashboard className="w-5 h-5 text-gray-500 group-hover:text-yellow-500 transition-colors duration-300" />
                      <span className="flex-1">Dashboard</span>
                      <span className="text-xs bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-300 px-2 py-1 rounded-full">
                        New
                      </span>
                    </Link>

                    <Link
                      to="/profile"
                      onClick={toggleDropdown}
                      className="flex items-center space-x-3 px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-yellow-50 dark:hover:bg-gray-700 rounded-lg transition-all duration-300 group"
                    >
                      <FaUserCircle className="w-5 h-5 text-gray-500 group-hover:text-yellow-500 transition-colors duration-300" />
                      <span className="flex-1">Profile</span>
                    </Link>

                    <Link
                      to="/job"
                      onClick={toggleDropdown}
                      className="flex items-center space-x-3 px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-yellow-50 dark:hover:bg-gray-700 rounded-lg transition-all duration-300 group"
                    >
                      <HiDocument className="w-5 h-5 text-gray-500 group-hover:text-yellow-500 transition-colors duration-300" />
                      <span className="flex-1">Job Application's</span>
                    </Link>


                    <Link
                      to="/inquiry"
                      onClick={toggleDropdown}
                      className="flex items-center space-x-3 px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-yellow-50 dark:hover:bg-gray-700 rounded-lg transition-all duration-300 group"
                    >
                      <MdSecurity className="w-5 h-5 text-gray-500 group-hover:text-yellow-500 transition-colors duration-300" />
                      <span className="flex-1">Enquiry</span>
                    </Link>

                    <div className="border-t border-gray-100 dark:border-gray-700 my-2"></div>

                    {/* Logout Button */}
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-3 px-4 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-300 group"
                    >
                      <FaSignOutAlt className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                      <span className="flex-1 text-left">Sign Out</span>
                      <span className="text-xs text-gray-400">⌘Q</span>
                    </button>
                  </div>

                  {/* Footer */}
                  <div className="p-3 bg-gray-50 dark:bg-gray-900 text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Version 2.0.0 • © 2024
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Spacer to prevent content from hiding behind navbar */}
      <div className="h-20"></div>

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideDown {
          animation: slideDown 0.2s ease-out;
        }
      `}</style>
    </>
  );
};

export default AuthNavbar;